import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TrendingUp, Infinity as InfinityIcon, Building2, Headset, Ticket } from "lucide-react";

const GAMES = [
  { id: "crash", name: "CRASH", to: "/game", icon: TrendingUp, desc: "Multiplayer curve — cash out in time." },
  { id: "limbo", name: "LIMBO", to: "/limbo", icon: InfinityIcon, desc: "Pick a target, instant roll." },
  { id: "tower", name: "TOWER", to: "/tower", icon: Building2, desc: "Climb 8 floors, dodge the trap." },
];

export default function Entertainment() {
  const [movies, setMovies] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [busy, setBusy] = useState(null);

  const loadTickets = () => api.get("/cinema/tickets/me").then(({ data }) => setTickets(data)).catch(() => {});

  useEffect(() => {
    api.get("/cinema/movies").then(({ data }) => setMovies(data)).catch(() => {});
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    loadTickets();
  }, []);

  const buyTicket = async (movieId, showtime) => {
    setBusy(`${movieId}-${showtime}`);
    try {
      const { data } = await api.post("/cinema/ticket", { movie_id: movieId, showtime });
      setWallet((w) => ({ ...w, balance_usdt: data.balance_usdt }));
      toast.success(`Ticket booked — ${data.title} ${data.showtime} · seat ${data.seat}`);
      loadTickets();
    } catch (err) {
      toast.error(apiError(err, "Ticket purchase failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="entertainment-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="label-mono text-brand-cyan mb-2">// entertainment_district</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">PLAY & WATCH.</h1>
          <p className="text-white/50 font-body mt-1">Games, the forest cinema and the VR plaza.</p>
        </div>
        <div className="panel px-5 py-3">
          <span className="label-mono mr-3">Wallet</span>
          <span className="font-mono text-xl font-bold text-brand-magenta">{wallet.balance_usdt?.toFixed(2)} USDT</span>
        </div>
      </div>

      <section className="mb-12">
        <div className="label-mono mb-4">// 01_games</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ent-games-grid">
          {GAMES.map((g) => {
            const Icon = g.icon;
            return (
              <Link key={g.id} to={g.to} className="panel panel-glow p-5 group hover:-translate-y-1 transition-transform" data-testid={`ent-game-${g.id}`}>
                <div className="w-12 h-12 border-2 border-brand-magenta grid place-items-center mb-4">
                  <Icon className="w-6 h-6 text-brand-magenta" />
                </div>
                <div className="font-display font-black text-2xl tracking-tighter">{g.name}</div>
                <p className="text-white/50 text-xs font-body mt-1">{g.desc}</p>
                <div className="label-mono text-brand-magenta mt-3 group-hover:translate-x-1 transition-transform inline-block">play →</div>
              </Link>
            );
          })}
        </div>
        <Link to="/games" className="label-mono text-white/40 hover:text-white inline-block mt-3" data-testid="ent-all-games-link">view all worlds →</Link>
      </section>

      <section className="mb-12">
        <div className="label-mono mb-4">// 02_forest_cinema · tickets 3 USDT</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cinema-grid">
          {movies.map((m) => (
            <div key={m.id} className="panel overflow-hidden group" data-testid={`movie-card-${m.id}`}>
              <div className="relative aspect-[2/3] overflow-hidden">
                <img src={m.img} alt={m.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute bottom-2 left-3 right-3">
                  <div className="font-display font-bold">{m.title}</div>
                  <div className="text-[11px] font-mono text-white/50">{m.genre} · {m.duration}</div>
                </div>
              </div>
              <div className="p-3">
                <div className="label-mono mb-2">showtimes</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {m.showtimes.map((t) => (
                    <button
                      key={t}
                      onClick={() => buyTicket(m.id, t)}
                      disabled={busy === `${m.id}-${t}`}
                      className="py-1.5 border border-neon-line text-white/60 hover:border-brand-magenta hover:text-white font-mono text-[11px] transition-colors"
                      data-testid={`ticket-${m.id}-${t.replace(":", "")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <Link to="/plaza" className="panel panel-glow p-6 flex items-center justify-between group hover:-translate-y-0.5 transition-transform" data-testid="ent-vr-plaza-link">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border-2 border-brand-cyan grid place-items-center">
              <Headset className="w-6 h-6 text-brand-cyan" />
            </div>
            <div>
              <div className="font-display font-black text-xl">VR PLAZA</div>
              <p className="text-white/50 text-sm font-body">3D social space — works with your VR headset (WebXR).</p>
            </div>
          </div>
          <span className="label-mono text-brand-cyan group-hover:translate-x-1 transition-transform">enter →</span>
        </Link>
      </section>

      <section>
        <div className="label-mono mb-4">// my_tickets</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-tickets-list">
          {tickets.length === 0 && <p className="text-white/40 text-sm font-mono">No tickets yet.</p>}
          {tickets.map((t) => (
            <div key={t.ticket_id} className="panel p-4 flex items-center gap-3">
              <Ticket className="w-5 h-5 text-brand-amber shrink-0" />
              <div className="flex-1">
                <div className="font-display font-bold text-sm">{t.title}</div>
                <div className="text-xs font-mono text-white/50">{t.showtime} · seat {t.seat}</div>
              </div>
              <span className="font-mono text-xs text-brand-magenta">{t.price} USDT</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
